import { createSelector } from 'reselect';
import { ApplicationRootState } from 'types';
import { initialState } from './reducer';
import {
  makeSelectShelvesSelected,
  makeSelectShelvesSelectedLastCabinet,
} from 'containers/Configurator/selectors/selection';
import { makeSelectDoorHandles } from 'containers/Configurator/selectors/pieces';
import { Color } from 'lib/modelBuilder/entities/Color';
import { makeSelectColors } from 'containers/Configurator/selectors/colors';
import { ECO_COLOR, ECO_PLUS_COLOR } from 'lib/modelBuilder/constants';
import ItemPicker from 'helpers/ItemPicker';
import _ from 'lodash';
import {
  makeSelectCounterSelectedISInShelvesSelected,
  makeSelectIsSameIntermediateShelvesInShelvesSelected,
  makeSelectISSelection,
} from 'containers/IntermediateShelfPanel/selectors';

/**
 * Direct selector to the drawersPanel state domain
 */

export const selectDrawersPanelDomain = (state: ApplicationRootState) =>
  state.drawersPanel || initialState;

export const makeSelectIsVisibleDrawersPanel = () =>
  createSelector(selectDrawersPanelDomain, substate => substate.isVisible);

export const makeSelectDrawersSelected = () =>
  createSelector(
    selectDrawersPanelDomain,
    substate => substate.drawersSelected,
  );

export const makeSelectIsSameDrawersNumberInShelvesSelected = () =>
  createSelector(
    makeSelectShelvesSelectedLastCabinet(),
    (shelvesSelected): boolean => {
      let counter = -1;
      for (const shelf of shelvesSelected) {
        if (counter !== shelf.drawers.length) {
          if (counter === -1) counter = shelf.drawers.length;
          else {
            return false;
          }
        }
      }
      return true;
    },
  );

export const makeSelectCounterSelectedDrawersInShelvesSelected = () =>
  createSelector(
    makeSelectShelvesSelectedLastCabinet(),
    makeSelectIsSameDrawersNumberInShelvesSelected(),
    (shelvesSelected, sameDrawersNumber): number | null => {
      if (!sameDrawersNumber || shelvesSelected.length === 0) return null;
      return shelvesSelected[0].drawers.length;
    },
  );

export const makeSelectAllDrawersSelectionArray = () =>
  createSelector(
    makeSelectCounterSelectedDrawersInShelvesSelected(),
    (counter): number[] | null => {
      if (!counter) return null;
      const drawerSelected = [];
      if (counter > 0) {
        for (let i = 0; i < counter; i++) {
          drawerSelected.push(i);
        }
      }
      return drawerSelected;
    },
  );

export const makeSelectMDFColorItems = () =>
  createSelector(makeSelectColors(), colorsData => {
    const colors: ItemPicker[] = [];
    for (const item of colorsData) {
      if (item.order < 100)
        colors.push(new ItemPicker(item.name, undefined, item));
    }
    return colors;
  });

export const makeSelectEcoColors = () =>
  createSelector(makeSelectColors(), colorsData => {
    const items: ItemPicker[] = [];
    for (const item of colorsData) {
      if (item.reference === ECO_COLOR || item.reference === ECO_PLUS_COLOR) {
        let imgPath = `/configurator/assets/images/drawers/eco.png`;
        if (item.reference === ECO_PLUS_COLOR) {
          imgPath = `/configurator/assets/images/drawers/ecoPlus.png`;
        }
        items.push(new ItemPicker(item.name, imgPath, item));
      }
    }
    return items;
  });

export const makeSelectFullDrawersSelection = () =>
  createSelector(makeSelectShelvesSelectedLastCabinet(), (shelves): boolean => {
    for (const shelf of shelves) {
      if (!shelf.drawers || shelf.drawers.length === 0) {
        return false;
      }
    }
    return true;
  });

export const makeSelectAsADrawerInSelection = () =>
  createSelector(makeSelectShelvesSelected(), (shelves): boolean => {
    for (const shelf of shelves) {
      if (shelf.drawers && shelf.drawers.length > 0) {
        return true;
      }
    }
    return false;
  });

export const makeSelectCommonColorDrawers = () =>
  createSelector(
    makeSelectShelvesSelectedLastCabinet(),
    makeSelectIsSameDrawersNumberInShelvesSelected(),
    makeSelectCounterSelectedDrawersInShelvesSelected(),
    (
      shelvesSelected,
      sameDrawersNumberInSelection,
      counterDrawers,
    ): (Color | undefined)[] | undefined => {
      if (!counterDrawers || counterDrawers === 0) {
        return undefined;
      }
      const colorsArray: (Color | undefined)[] = [];
      for (const i of _.range(0, counterDrawers)) {
        for (const shelf of shelvesSelected) {
          const entry = shelf.getDrawer(i);
          if (colorsArray[i]) {
            if (colorsArray[i] !== entry?.color) {
              colorsArray[i] = undefined;
              break;
            }
          } else {
            colorsArray[i] = entry?.color;
          }
        }
      }

      return colorsArray;
    },
  );
